import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, FileText, Wheat, Eye } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, openFileById } from "@/lib/api";
import { maskId } from "@/lib/mask";
import { HEADER, INFRA_ITEMS, SOCIAL_ITEMS } from "@/mock/data";

const STATUS_BADGE = {
  SUBMITTED: "bg-blue-50 text-blue-700 border-blue-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-red-50 text-red-700 border-red-200",
  DRAFT: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_LABEL = { SUBMITTED: "सादर केला", APPROVED: "मंजूर", REJECTED: "नामंजूर", DRAFT: "प्रारूप" };

function isFile(v) {
  return v && typeof v === "object" && v.fileId;
}
function fmt(v) {
  if (v === undefined || v === null || v === "" || v === "-- निवडा --") return "—";
  if (isFile(v)) return v.name;
  if (typeof v === "object") return v.name || "—";
  if (v === "yes") return "आहे";
  if (v === "no") return "नाही";
  return String(v);
}

function openFile(fileId, name) { openFileById(fileId, name); }

function Row({ label, value }) {
  const file = isFile(value);
  return (
    <div className="flex gap-2 border-b border-dotted border-slate-200 py-1.5 text-[13.5px]">
      <span className="min-w-[190px] text-slate-500">{label}:</span>
      {file ? (
        <button onClick={() => openFile(value.fileId, value.name)} className="inline-flex items-center gap-1 font-semibold text-[#1b5e3f] hover:underline">
          <Eye className="h-3.5 w-3.5" /> {value.name}
        </button>
      ) : (
        <span className="font-semibold text-slate-800">{fmt(value)}</span>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-slate-200 bg-white">
      <h3 className="bg-slate-50 px-4 py-2.5 text-[15px] font-bold text-[#1b5e3f]">{title}</h3>
      <div className="grid grid-cols-1 gap-x-8 px-4 py-3 md:grid-cols-2">{children}</div>
    </div>
  );
}

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useApp();
  const [appn, setAppn] = useState(null);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const a = await api.adminDetail(id);
        setAppn(a);
        setRemark(a.adminRemark || "");
      } catch (e) {
        toast.error("अर्ज लोड करता आला नाही.");
      }
    })();
  }, [id]);

  const review = async (action) => {
    setBusy(true);
    try {
      const updated = await api.adminReview(id, { action, remark });
      setAppn(updated);
      toast.success(action === "approve" ? "अर्ज मंजूर झाला ✓" : "अर्ज नामंजूर झाला");
    } catch (e) {
      toast.error(e.message || "कृती अयशस्वी.");
    } finally {
      setBusy(false);
    }
  };

  if (!appn) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f6f2e6] text-slate-500">लोड होत आहे...</div>;
  }

  const d = appn.data || {};

  return (
    <div className="min-h-screen bg-[#f6f2e6]">
      <header className="sticky top-0 z-40 text-white shadow-md" style={{ background: "linear-gradient(120deg,#1b5e3f 0%,#2d7a52 100%)" }}>
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3.5">
          <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-[14px] font-medium hover:opacity-90">
            <ArrowLeft className="h-4 w-4" /> डॅशबोर्ड
          </button>
          <div className="flex items-center gap-2">
            <Wheat className="h-5 w-5 text-[#E0A106]" strokeWidth={2} />
            <span className="text-[15px] font-bold">ASSK Sangli</span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-5 py-6">
        {/* Summary */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <div>
            <p className="text-[12px] text-slate-500">अर्ज क्रमांक</p>
            <p className="text-[22px] font-bold text-[#1b5e3f] font-latin">{appn.applicationNumber}</p>
            <p className="mt-1 text-[14px] font-semibold text-slate-700">{d.nameMr || appn.applicantName}</p>
            <p className="text-[12.5px] text-slate-400">{appn.applicantEmail}</p>
          </div>
          <div className="text-right">
            <span className={`rounded-full border px-4 py-1.5 text-[13px] font-semibold ${STATUS_BADGE[appn.status]}`}>
              {STATUS_LABEL[appn.status]}
            </span>
            {appn.submittedAt && (
              <p className="mt-2 text-[12px] text-slate-500 font-latin">
                सादर: {new Date(appn.submittedAt).toLocaleString("en-GB", { hour12: false }).replace(",", "")}
              </p>
            )}
            {appn.reviewedBy && (
              <p className="mt-1 text-[12px] text-slate-500">पुनरावलोकन: {appn.reviewedBy}</p>
            )}
          </div>
        </div>

        <Section title="१. वैयक्तिक माहिती">
          <Row label="संस्थेचा प्रकार" value={d.orgType} />
          <Row label="तालुका" value={d.taluka} />
          <Row label="ग्रामपंचायत / गाव" value={d.village} />
          <Row label="नाव (मराठी)" value={d.nameMr} />
          <Row label="नाव (इंग्रजी)" value={d.nameEn} />
          <Row label="रहिवासाचा पत्ता" value={d.address} />
          <Row label="गाव/शहर" value={d.city} />
          <Row label="वॉर्ड क्रमांक" value={d.wardNo} />
          <Row label="पिनकोड" value={d.pincode} />
          <Row label="जन्म तारीख" value={d.dob} />
          <Row label="वय" value={d.age} />
          <Row label="लिंग" value={d.gender} />
          <Row label="मोबाईल" value={d.mobile} />
          <Row label="ई-मेल" value={d.email} />
          <Row label="आधार क्रमांक" value={maskId(d.aadhaar)} />
          <Row label="पॅन क्रमांक" value={maskId(d.pan)} />
          <Row label="आधारकार्ड अपलोड" value={d.aadhaar_file} />
          <Row label="पॅनकार्ड अपलोड" value={d.pan_file} />
          <Row label="स्वघोषणापत्र" value={d.self_declaration_file} />
          <Row label="रहिवासी पत्त्याचा पुरावा" value={d.residence_proof_file} />
          <Row label="फोटो" value={d.photo} />
        </Section>

        <Section title="२. शैक्षणिक माहिती">
          <Row label="१२ वी (HSC) बोर्ड" value={d.edu_board_0} />
          <Row label="उत्तीर्ण वर्ष" value={d.edu_year_0} />
          <Row label="टक्केवारी" value={d.edu_pct_0} />
          <Row label="श्रेणी" value={d.edu_class_0} />
          <Row label="HSC प्रमाणपत्र" value={d.edu_file_0} />
        </Section>

        <Section title="३. संगणक साक्षरता">
          <Row label="प्रमाणपत्र १ (नाव)" value={d.cl_name_0} />
          <Row label="प्रमाणपत्र १ (वर्ष)" value={d.cl_year_0} />
          <Row label="प्रमाणपत्र १ (टक्केवारी)" value={d.cl_pct_0} />
          <Row label="प्रमाणपत्र १ अपलोड" value={d.cl_file_0} />
          <Row label="प्रमाणपत्र २ (नाव)" value={d.cl_name_1} />
          <Row label="प्रमाणपत्र २ अपलोड" value={d.cl_file_1} />
          <Row label="प्रमाणपत्र ३ (नाव)" value={d.cl_name_2} />
          <Row label="प्रमाणपत्र ३ अपलोड" value={d.cl_file_2} />
        </Section>

        <Section title="४. पायाभूत सुविधा">
          {INFRA_ITEMS.map((it, i) => (
            <Row key={i} label={`${i + 1}. ${it.label}`} value={d[`infra_${i}`]} />
          ))}
        </Section>

        <Section title="५. आर्थिक क्षमता">
          <Row label="आर्थिक क्षमता" value={d.finProof} />
          <Row label="Bank statements" value={d.fin_detail_0} />
          <Row label="Bank statements — पुरावा" value={d.fin_file_0} />
          <Row label="Fixed Deposits" value={d.fin_detail_1} />
          <Row label="Fixed Deposits — पुरावा" value={d.fin_file_1} />
          <Row label="Investment Details" value={d.fin_detail_2} />
          <Row label="Investment — पुरावा" value={d.fin_file_2} />
          <Row label="Others" value={d.fin_detail_3} />
          <Row label="Others — पुरावा" value={d.fin_file_3} />
        </Section>

        <Section title="६. अनुभव">
          <Row label="CSC" value={d.cscExp} />
          <Row label="बँकिंग प्रतिनिधी" value={d.bankRep} />
          <Row label="ई-गव्हर्नन्स अनुभव" value={d.egovExp} />
          <Row label="यापूर्वी सेवा केंद्र" value={d.prevCenter} />
        </Section>

        <Section title="७. व्यवसाय आराखडा">
          <Row label="सेवा विस्तार" value={d.bp_text_0} />
          <Row label="जनजागृती" value={d.bp_text_1} />
          <Row label="आर्थिक व्यवहार्यता" value={d.bp_text_2} />
          <Row label="व्यवसाय नियोजन" value={d.bp_text_3} />
          <Row label="आराखडा दस्तऐवज (PDF)" value={d.bp_pdf} />
        </Section>

        <Section title="८. सामाजिक समावेशकता">
          {SOCIAL_ITEMS.map((it) => (
            <Row key={it.key} label={it.label} value={d[it.key]} />
          ))}
        </Section>

        <Section title="९. चारित्र्य पडताळणी">
          <Row label="पोलीस ठाणे" value={d.cv_station} />
          <Row label="क्रमांक" value={d.cv_number} />
          <Row label="दिनांक" value={d.cv_date} />
          <Row label="प्रमाणपत्र" value={d.cv_file} />
          <Row label="आर्थिक गैरव्यवहार" value={d.cv_fraud} />
          <Row label="काळ्या यादीत" value={d.cv_blacklist} />
          <Row label="प्रतिज्ञापत्र" value={d.cv_affidavit_file} />
        </Section>

        <Section title="१०. अर्ज शुल्क">
          <Row label="UTR क्रमांक" value={d.fee_utr} />
          <Row label="पेमेंटची पद्धत" value={d.fee_method} />
          <Row label="पेमेंट पुरावा" value={d.fee_proof} />
        </Section>
      </div>
    </div>
  );
}
